import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, SlidersHorizontal, X,
  ChevronLeft, ChevronRight, ArrowDownCircle,
  Landmark, CheckCircle2, XCircle, AlertCircle, Wallet,
} from 'lucide-react';
import { withdrawalsAPI } from '../../api/withdrawals';
import PageHeader  from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatPeriod = (val) => {
  if (!val) return '—';
  return val.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const STATUSES  = ['all', 'pending', 'approved', 'rejected'];
const PER_PAGE  = 15;

// ── Status tab style ──────────────────────────────────────────
const statusTabStyle = (current, s) => ({
  background: current === s
    ? s === 'pending'  ? 'rgba(245,158,11,0.3)'
    : s === 'approved' ? 'rgba(5,150,105,0.3)'
    : s === 'rejected' ? 'rgba(239,68,68,0.3)'
    : 'linear-gradient(135deg,#d4a017,#f0c040)'
    : 'rgba(255,255,255,0.07)',
  color: current === s
    ? s === 'pending'  ? '#fcd34d'
    : s === 'approved' ? '#6ee7b7'
    : s === 'rejected' ? '#fca5a5'
    : '#fff'
    : 'rgba(255,255,255,0.5)',
  border: current === s ? 'none' : '1px solid rgba(255,255,255,0.1)',
});

// ── Skeleton ─────────────────────────────────────────────────
function WithdrawalSkeleton() {
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="skeleton h-10 w-10 flex-shrink-0 rounded-full" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="skeleton h-3.5 w-28 rounded" />
          <div className="skeleton h-3 w-20 rounded" />
        </div>
        <div className="skeleton h-5 w-20 rounded" />
      </div>
      <div className="skeleton h-10 rounded-xl" />
    </div>
  );
}

// ── Action Sheet ─────────────────────────────────────────────
function ActionSheet({ withdrawal, onClose, onSuccess }) {
  const [action,     setAction]     = useState(null); // 'approved' | 'rejected'
  const [remarks,    setRemarks]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState('');

  const handleConfirm = async () => {
    setSubmitting(true);
    setApiError('');
    try {
      const { data } = await withdrawalsAPI.updateStatus(withdrawal.id, {
        status: action,
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      });
      if (data.status) {
        onSuccess(withdrawal.id, data.data.status, data.data.remarks);
        onClose();
      } else {
        setApiError(data.message || 'Update failed.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message || 'Something went wrong.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isApprove          = action === 'approved';
  const userBalance        = parseFloat(withdrawal.user?.balance ?? 0);
  const withdrawalAmount   = parseFloat(withdrawal.amount ?? 0);
  const insufficientFunds  = userBalance < withdrawalAmount;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-serif text-base font-semibold text-white">
              Withdrawal Request
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {withdrawal.user?.name ?? 'Deleted User'} · {formatINR(withdrawal.amount)}
            </p>
          </div>
          {!submitting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* User balance row */}
        <div className="mb-3 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
             style={{
               background: insufficientFunds ? 'rgba(239,68,68,0.1)' : 'rgba(5,150,105,0.1)',
               border: `1px solid ${insufficientFunds ? 'rgba(239,68,68,0.3)' : 'rgba(5,150,105,0.25)'}`,
             }}>
          <div className="flex items-center gap-2">
            <Wallet size={14} style={{ color: insufficientFunds ? '#fca5a5' : '#6ee7b7', flexShrink: 0 }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>User balance</span>
          </div>
          <span className="text-sm font-bold"
                style={{ color: insufficientFunds ? '#fca5a5' : '#6ee7b7' }}>
            {formatINR(withdrawal.user?.balance ?? 0)}
          </span>
        </div>

        {/* Insufficient balance warning */}
        {insufficientFunds && (
          <div className="mb-3 flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
            <span>
              Insufficient balance — user only has {formatINR(withdrawal.user?.balance ?? 0)} but
              requested {formatINR(withdrawal.amount)}. Approval will be blocked.
            </span>
          </div>
        )}

        {/* Bank account summary */}
        {withdrawal.user?.bank_account && (
          <div className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3"
               style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Landmark size={15} style={{ color: '#d4a017', flexShrink: 0 }} />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white truncate">
                {withdrawal.user.bank_account.bank_name}
              </p>
              <p className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {withdrawal.user.bank_account.account_number} · {withdrawal.user.bank_account.ifsc}
              </p>
            </div>
          </div>
        )}

        {/* API error */}
        {apiError && (
          <div className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="flex-shrink-0" />
            <span>{apiError}</span>
          </div>
        )}

        {/* Choose action */}
        {!action ? (
          <div className="flex gap-3">
            <button
              onClick={() => !insufficientFunds && setAction('approved')}
              disabled={insufficientFunds}
              className="flex-1 flex items-center justify-center gap-2 rounded-2xl
                         py-3.5 text-sm font-semibold transition-all active:scale-95
                         disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: insufficientFunds
                  ? 'rgba(5,150,105,0.2)'
                  : 'linear-gradient(135deg,#059669 0%,#047857 100%)',
                boxShadow: insufficientFunds ? 'none' : '0 4px 16px rgba(5,150,105,0.3)',
                color: '#fff',
              }}
            >
              <CheckCircle2 size={15} /> Approve
            </button>
            <button
              onClick={() => setAction('rejected')}
              className="flex-1 flex items-center justify-center gap-2 rounded-2xl
                         py-3.5 text-sm font-semibold transition-all active:scale-95"
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
              }}
            >
              <XCircle size={15} /> Reject
            </button>
          </div>
        ) : (
          /* Confirm step with remarks */
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5"
                 style={{
                   background: isApprove ? 'rgba(5,150,105,0.12)' : 'rgba(239,68,68,0.12)',
                   border: `1px solid ${isApprove ? 'rgba(5,150,105,0.3)' : 'rgba(239,68,68,0.3)'}`,
                 }}>
              {isApprove
                ? <CheckCircle2 size={14} style={{ color: '#6ee7b7', flexShrink: 0 }} />
                : <XCircle      size={14} style={{ color: '#fca5a5', flexShrink: 0 }} />}
              <span className="text-xs font-semibold"
                    style={{ color: isApprove ? '#6ee7b7' : '#fca5a5' }}>
                {isApprove ? 'Approving' : 'Rejecting'} — {formatINR(withdrawal.amount)}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="input-label">
                Remarks <span style={{ color: 'rgba(255,255,255,0.3)' }}>(optional)</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={isApprove
                  ? 'e.g. Amount transferred to account'
                  : 'e.g. Insufficient balance'}
                rows={2}
                maxLength={500}
                disabled={submitting}
                className="input-field resize-none disabled:opacity-50"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setAction(null); setRemarks(''); setApiError(''); }}
                disabled={submitting}
                className="flex-1 rounded-2xl py-3 text-sm font-semibold
                           transition-all active:scale-95 disabled:opacity-50"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting || (isApprove && insufficientFunds)}
                className="flex-1 inline-flex items-center justify-center gap-2
                           rounded-2xl py-3 text-sm font-semibold transition-all
                           active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: isApprove
                    ? 'linear-gradient(135deg,#059669 0%,#047857 100%)'
                    : 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)',
                  color: '#fff',
                }}
              >
                {submitting
                  ? <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  : `Confirm ${isApprove ? 'Approval' : 'Rejection'}`
                }
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Withdrawal card ──────────────────────────────────────────
function WithdrawalCard({ withdrawal, onAction }) {
  const userName = withdrawal.user?.name ?? 'Deleted User';
  const initials = userName
    .split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const isPending = withdrawal.status === 'pending';

  return (
    <div className="card flex flex-col gap-3 transition-all duration-200">

      {/* Top row — user + amount */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center
                        rounded-full text-xs font-bold text-white"
             style={{ background: 'linear-gradient(135deg,#1448c4 0%,#0a2260 100%)' }}>
          {initials}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {userName}
          </p>
          <p className="text-xs mt-0.5 truncate"
             style={{ color: 'rgba(255,255,255,0.45)' }}>
            {withdrawal.user?.mobile ?? '—'}
          </p>
        </div>

        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-bold text-gold-shimmer">
            {formatINR(withdrawal.amount)}
          </p>
          <span className="inline-block mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
            {formatPeriod(withdrawal.withdrawal_period)}
          </span>
        </div>
      </div>

      {/* Bank account strip */}
      {withdrawal.user?.bank_account && (
        <div className="flex items-center gap-2 rounded-xl px-3 py-2"
             style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Landmark size={12} style={{ color: '#d4a017', flexShrink: 0 }} />
          <p className="text-[11px] truncate flex-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {withdrawal.user.bank_account.bank_name} ·{' '}
            <span style={{ color: 'rgba(255,255,255,0.7)' }}>
              {withdrawal.user.bank_account.account_number}
            </span>
          </p>
        </div>
      )}

      {/* Bottom row — date + status + action */}
      <div className="flex items-center justify-between">
        <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {formatDate(withdrawal.requested_at)}
        </p>
        <div className="flex items-center gap-2">
          <StatusBadge status={withdrawal.status} />
          {isPending && (
            <button
              onClick={() => onAction(withdrawal)}
              className="rounded-xl px-3 py-1 text-xs font-semibold transition-all active:scale-95"
              style={{
                background: 'rgba(212,160,23,0.15)',
                border: '1px solid rgba(212,160,23,0.3)',
                color: '#d4a017',
              }}
            >
              Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function WithdrawalsPage() {
  const [searchParams] = useSearchParams();

  // filters — initialise status from ?status= query param if present
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState(() => {
    const s = searchParams.get('status');
    return STATUSES.includes(s) ? s : 'all';
  });
  const [from,        setFrom]        = useState('');
  const [to,          setTo]          = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // data
  const [withdrawals, setWithdrawals] = useState([]);
  const [pagination,  setPagination]  = useState(null);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  // action sheet
  const [activeWithdrawal, setActiveWithdrawal] = useState(null);
  const [successMsg,       setSuccessMsg]       = useState('');

  // debounce search
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  useEffect(() => { setPage(1); }, [status, from, to]);

  // ── Fetch ─────────────────────────────────────────────────
  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: PER_PAGE };
      if (debouncedSearch)  params.search = debouncedSearch;
      if (status !== 'all') params.status = status;
      if (from)             params.from   = from;
      if (to)               params.to     = to;

      const { data } = await withdrawalsAPI.getList(params);
      if (data.status) {
        setWithdrawals(data.data.withdrawals);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load withdrawals.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, from, to, page]);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Update card in-place after action
  const handleActionSuccess = (id, newStatus, remarks) => {
    setWithdrawals((prev) =>
      prev.map((w) => w.id === id ? { ...w, status: newStatus, remarks } : w)
    );
    setSuccessMsg(
      `Withdrawal ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`
    );
  };

  const clearFilters = () => { setStatus('all'); setFrom(''); setTo(''); setPage(1); };
  const hasActiveFilters = status !== 'all' || from || to;

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Withdrawals"
        right={
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="relative flex h-8 w-8 items-center justify-center rounded-xl
                       transition-all active:scale-90"
            style={{
              background: showFilters ? 'rgba(212,160,23,0.2)' : 'rgba(255,255,255,0.08)',
              border: showFilters
                ? '1px solid rgba(212,160,23,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <SlidersHorizontal size={15}
              style={{ color: showFilters ? '#d4a017' : 'rgba(255,255,255,0.6)' }} />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                    style={{ background: '#d4a017' }} />
            )}
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* ── Success banner ── */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{
                 background: 'rgba(5,150,105,0.15)',
                 border: '1px solid rgba(5,150,105,0.35)',
                 color: '#6ee7b7',
               }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.35)' }} />
          <input
            type="text"
            placeholder="Search by user name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-9"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="card flex flex-col gap-4 animate-fade-in">
            <div>
              <p className="input-label mb-2">Status</p>
              <div className="flex gap-2 flex-wrap">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className="rounded-xl px-3 py-1.5 text-xs font-semibold capitalize
                               transition-all duration-150"
                    style={statusTabStyle(status, s)}
                  >
                    {s === 'all' ? 'All' : s}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="input-label">From</label>
                <input type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)}
                       className="input-field" />
              </div>
              <div className="flex flex-col">
                <label className="input-label">To</label>
                <input type="date" value={to}
                       onChange={(e) => setTo(e.target.value)}
                       className="input-field" />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters}
                      className="flex items-center gap-1.5 self-start text-xs font-medium"
                      style={{ color: '#fca5a5' }}>
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Active status pill ── */}
        {!showFilters && status !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>Filtered:</span>
            <StatusBadge status={status} />
            <button onClick={clearFilters} className="ml-auto" style={{ color: '#fca5a5' }}>
              <X size={12} />
            </button>
          </div>
        )}

        {/* ── Summary ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing {withdrawals.length} of {pagination.total} withdrawal{pagination.total !== 1 ? 's' : ''}
          </p>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => <WithdrawalSkeleton key={i} />)}
          </div>
        )}

        {/* ── Withdrawal list ── */}
        {!loading && !error && withdrawals.length > 0 && (
          <div className="flex flex-col gap-3">
            {withdrawals.map((w) => (
              <WithdrawalCard
                key={w.id}
                withdrawal={w}
                onAction={setActiveWithdrawal}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && withdrawals.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <ArrowDownCircle size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {debouncedSearch || hasActiveFilters
                ? 'No withdrawals match your filters'
                : 'No withdrawal requests found'}
            </p>
            {(debouncedSearch || hasActiveFilters) && (
              <button
                onClick={() => { setSearch(''); clearFilters(); }}
                className="text-xs font-medium"
                style={{ color: '#d4a017' }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>

      {/* ── Action sheet (API #11) ── */}
      {activeWithdrawal && (
        <ActionSheet
          withdrawal={activeWithdrawal}
          onClose={() => setActiveWithdrawal(null)}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
  );
}
